import { CALL_CSR_API } from '../../middleware/authHeaders'
import { CSR_API_ROOT } from '../../config/api'
import { errorNotification, successNotification } from '../application/notifications'
import csrResponseHasError from '../../components/csr/authUtils'

// TYPES & ACTIONS
export const CSR_GET_ORGANIZATIONS_REQUEST = 'GET_ORGANIZATIONS_REQUEST'
export const CSR_GET_ORGANIZATIONS_SUCCESS = 'GET_ORGANIZATIONS_SUCCESS'
export const CSR_GET_ORGANIZATIONS_FAILURE = 'GET_ORGANIZATIONS_FAILURE'

export const getOrganizations = () => dispatch => {
  dispatch({
    [CALL_CSR_API]: {
      method: 'GET',
      endpoint: `${CSR_API_ROOT}/organization/list`,
      types: [
        CSR_GET_ORGANIZATIONS_REQUEST,
        CSR_GET_ORGANIZATIONS_SUCCESS,
        CSR_GET_ORGANIZATIONS_FAILURE,
      ],
    },
  })
}

export const CSR_UPLOAD_ORGANIZATION_LOGO_REQUEST = 'CSR_UPLOAD_ORGANIZATION_LOGO_REQUEST'
export const CSR_UPLOAD_ORGANIZATION_LOGO_SUCCESS = 'CSR_UPLOAD_ORGANIZATION_LOGO_SUCCESS'
export const CSR_UPLOAD_ORGANIZATION_LOGO_FAILURE = 'CSR_UPLOAD_ORGANIZATION_LOGO_FAILURE'

const uploadOrganizationLogo = (imageByteCode, orgId) => dispatch => {
  dispatch({
    [CALL_CSR_API]: {
      method: 'POST',
      endpoint: `${CSR_API_ROOT}/organization/${orgId}/image`,
      body: JSON.stringify({
        imageType: 'ORGANIZATION_LOGO_IMG',
        imageByteCode,
      }),
      types: [
        CSR_UPLOAD_ORGANIZATION_LOGO_REQUEST,
        CSR_UPLOAD_ORGANIZATION_LOGO_SUCCESS,
        CSR_UPLOAD_ORGANIZATION_LOGO_FAILURE,
      ],
    },
  }).then(response => {
    if (csrResponseHasError(response)) {
      dispatch(
        errorNotification({
          body: `Failed to upload logo image`,
          dismissInSeconds: 10,
        })
      )
    }
  })
}

export const CSR_CREATE_ORGANIZATION_REQUEST = 'CREATE_ORGANIZATION_REQUEST'
export const CSR_CREATE_ORGANIZATION_SUCCESS = 'CREATE_ORGANIZATION_SUCCESS'
export const CSR_CREATE_ORGANIZATION_FAILURE = 'CREATE_ORGANIZATION_FAILURE'

export const createOrganization =
  ({ companyLogo, companyLogoBase64String, ...payload }, successCallback) =>
  dispatch => {
    dispatch({
      [CALL_CSR_API]: {
        method: 'POST',
        endpoint: `${CSR_API_ROOT}/organization`,
        body: JSON.stringify(payload),
        types: [
          CSR_CREATE_ORGANIZATION_REQUEST,
          CSR_CREATE_ORGANIZATION_SUCCESS,
          CSR_CREATE_ORGANIZATION_FAILURE,
        ],
      },
    }).then(response => {
      // general errors handled globally
      if (!csrResponseHasError(response)) {
        dispatch(
          successNotification({
            body: `Successfully created organization and invited admin`,
            dismissInSeconds: 5,
          })
        )

        if (companyLogoBase64String) {
          dispatch(
            uploadOrganizationLogo(companyLogoBase64String, response.payload.results.organizationId)
          )
        }
        successCallback()
      }
    })
  }

export const editOrganization =
  ({ companyLogo, companyLogoBase64String, ...payload }, orgId, successCallback) =>
  dispatch => {
    dispatch({
      [CALL_CSR_API]: {
        method: 'PUT',
        endpoint: `${CSR_API_ROOT}/organization/${orgId}`,
        body: JSON.stringify(payload),
        types: [
          CSR_CREATE_ORGANIZATION_REQUEST,
          CSR_CREATE_ORGANIZATION_SUCCESS,
          CSR_CREATE_ORGANIZATION_FAILURE,
        ],
      },
    }).then(response => {
      // general errors handled globally
      if (!csrResponseHasError(response)) {
        dispatch(
          successNotification({
            body: `Successfully edited organization`,
            dismissInSeconds: 5,
          })
        )

        if (companyLogoBase64String) {
          dispatch(
            uploadOrganizationLogo(companyLogoBase64String, response.payload.results.organizationId)
          )
        }
        successCallback()
      }
    })
  }

export const CSR_GET_ORGANIZATION_BY_ID_REQUEST = 'GET_ORGANIZATION_BY_ID_REQUEST'
export const CSR_GET_ORGANIZATION_BY_ID_SUCCESS = 'GET_ORGANIZATION_BY_ID_SUCCESS'
export const CSR_GET_ORGANIZATION_BY_ID_FAILURE = 'GET_ORGANIZATION_BY_ID_FAILURE'

export const getOrganizationById = orgId => dispatch => {
  dispatch({
    [CALL_CSR_API]: {
      method: 'GET',
      endpoint: `${CSR_API_ROOT}/organization/${orgId}`,
      types: [
        CSR_GET_ORGANIZATION_BY_ID_REQUEST,
        CSR_GET_ORGANIZATION_BY_ID_SUCCESS,
        CSR_GET_ORGANIZATION_BY_ID_FAILURE,
      ],
    },
  })
}

export const CSR_GET_ORGANIZATION_PAY_PERIODS_REQUEST = 'CSR_GET_ORGANIZATION_PAYMENTS_REQUEST'
export const CSR_GET_ORGANIZATION_PAY_PERIODS_SUCCESS = 'CSR_GET_ORGANIZATION_PAYMENTS_SUCCESS'
export const CSR_GET_ORGANIZATION_PAY_PERIODS_FAILURE = 'CSR_GET_ORGANIZATION_PAYMENTS_FAILURE'
export const getOrganizationPayPeriods =
  (orgId, fiscalYear, pageNumber, pageSize = 10) =>
  dispatch => {
    dispatch({
      [CALL_CSR_API]: {
        method: 'GET',
        endpoint: `${CSR_API_ROOT}/organization/${orgId}/pay-periods/?fiscalYear=${fiscalYear}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
        types: [
          CSR_GET_ORGANIZATION_PAY_PERIODS_REQUEST,
          CSR_GET_ORGANIZATION_PAY_PERIODS_SUCCESS,
          CSR_GET_ORGANIZATION_PAY_PERIODS_FAILURE,
        ],
      },
    })
  }

export const CSR_SET_CURRENT_ORG_ID = 'CSR_SET_CURRENT_ORG_ID'
export const setCurrentOrgId = orgId => ({
  type: CSR_SET_CURRENT_ORG_ID,
  payload: orgId,
})

export const setCurrentOrgDetails = orgDetails => ({
  type: CSR_GET_ORGANIZATION_BY_ID_SUCCESS,
  payload: orgDetails,
})

// SELECTORS
export const selectOrganizationSlice = state => state.csr.organization
export const selectOrganizations = state => selectOrganizationSlice(state).organizations
export const selectCurrentOrganization = state =>
  selectOrganizationSlice(state).currentOrganizationId
export const selectOrganizationDetails = state =>
  selectOrganizationSlice(state).organizationDetails.results
export const selectPayPeriods = state => selectOrganizationSlice(state).payPeriods

// STATE
const INITIAL_STATE = {
  currentOrganizationId: null,
  organizations: [],
  organizationDetails: {},
  payPeriods: {},
}

// REDUCERS
const organization = (state = INITIAL_STATE, { type, payload } = {}) =>
  ({
    [CSR_GET_ORGANIZATIONS_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      organizations: newPayload,
    }),
    [CSR_SET_CURRENT_ORG_ID]: (currentState, newPayload) => ({
      ...currentState,
      currentOrganizationId: newPayload,
      organizationDetails: {},
      payPeriods: {},
    }),
    [CSR_GET_ORGANIZATION_BY_ID_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      organizationDetails: newPayload,
    }),
    [CSR_GET_ORGANIZATION_PAY_PERIODS_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      payPeriods: newPayload?.results,
    }),
  }[type]?.(state, payload) || state)

export default organization
